/**
 * Settings Storage Utility
 * 
 * Centralized management of user preferences stored in localStorage.
 * All settings are stored under a single key as a JSON object.
 */

const SETTINGS_KEY = 'plet-settings';

// Default settings values
export const DEFAULT_SETTINGS = {
  // Trackpad/Mouse sensitivity (1 = normal, 2 = 2x sensitive, 0.5 = half)
  trackpadPanSensitivity: 1.0,
  trackpadZoomSensitivity: 1.0,
  
  // UI preferences
  sidebarWidth: 280,
  minimapMaxWidth: 250,
  minimapMaxHeight: 180,
  
  // View preferences
  defaultView: 'flowchart', // 'flowchart' | 'kanban' | 'table'
  
  // Auto-archive settings
  autoArchiveEnabled: true,  // Whether auto-archive is enabled
  autoArchiveDays: 7,        // Days of inactivity before auto-archiving completed tasks
};

/**
 * Load all settings from localStorage
 */
export function loadSettings() {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all keys exist
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to load settings:', e);
  }
  return { ...DEFAULT_SETTINGS };
}

/**
 * Save all settings to localStorage
 */
export function saveSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn('Failed to save settings:', e);
  }
}

/**
 * Get a single setting value
 */
export function getSetting(key) {
  const settings = loadSettings();
  return settings[key] ?? DEFAULT_SETTINGS[key];
}

/**
 * Update a single setting
 */
export function setSetting(key, value) {
  const settings = loadSettings();
  settings[key] = value;
  saveSettings(settings);
  return settings;
}

/**
 * Update multiple settings at once
 */
export function updateSettings(updates) {
  const settings = loadSettings();
  Object.assign(settings, updates);
  saveSettings(settings);
  return settings;
}

/**
 * Reset all settings to defaults
 */
export function resetSettings() {
  saveSettings({ ...DEFAULT_SETTINGS });
  return { ...DEFAULT_SETTINGS };
}

/**
 * Reset a single setting to default
 */
export function resetSetting(key) {
  const settings = loadSettings();
  settings[key] = DEFAULT_SETTINGS[key];
  saveSettings(settings);
  return settings;
}

